import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { Auth, onAuthStateChanged } from '@angular/fire/auth';
import { ActivatedRoute, Router } from '@angular/router';
import { Observable, OperatorFunction, Subscription } from 'rxjs';
import { debounceTime, map, tap } from 'rxjs/operators';
import { Navigation } from 'src/app/enums/navigation.enum';
import { Race } from 'src/app/models/race';
import { User } from 'src/app/models/user';
import { EntryService } from 'src/app/services/entry.service';
import { EventService } from 'src/app/services/event.service';
import { RaceService } from 'src/app/services/race.service';
import { UserService } from 'src/app/services/user.service';

@Component({
    selector: 'app-race',
    templateUrl: './race.component.html',
    styleUrls: ['./race.component.scss'],
    standalone: false
})
export class RaceComponent implements OnInit, OnDestroy {

  race: Race;
  events: Observable<any[]>;
  referees: Observable<User[]>;
  refereesPage = 1;
  refereesPageSize = 5;
  showPagination = false;
  searching = false;
  newReferee: User;
  isOwner = false;
  isCollapsed = true;
  signedIn = false;
  user;
  public auth = inject(Auth);
  private _users: User[];
  private _userUid: string;
  private _userSub: Subscription;
  private _usersSub: Subscription;
  private authSubscription?;
  constructor(
    private _route: ActivatedRoute,
    private _router: Router,
    private _raceService: RaceService,
    private _eventService: EventService,
    private _entryService: EntryService,
    private _userService: UserService,
  ) {
    this.authSubscription = onAuthStateChanged(this.auth, (u) => {
      if (u) {
        // User is signed in, see docs for a list of available properties
        // https://firebase.google.com/docs/reference/js/auth.user
        this.signedIn = true
        this.user = u
        // ...
      } else {
        // User is signed out
        this.signedIn = false
        this.user = null;
      }
    });
  }

  async ngOnInit(): Promise<void> {
    const id = this._route.snapshot.paramMap.get('id');
    (await this._raceService.get(id)).forEach((doc) => {
      this.race = Object.assign(new Race, doc.data());
      this.getEvents();
      this.getOwnerViews();
    });
  }
  ngOnDestroy(): void {
    if(this._userSub){ this._userSub.unsubscribe(); }
    if(this._usersSub){ this._usersSub.unsubscribe(); }
    if(this.authSubscription){ this.authSubscription(); }
  }
  getEvents(){
    this.events = this._eventService.getByRace(this.race.id);
  }
  getOwnerViews(){
    // only authenticated users can view Collection.Users (aka referees)
    if(this.auth.currentUser?.uid){
      this._userUid = this.auth.currentUser.uid;
      this.getReferees();
      this.getUsers();
    }
    // this._userSub = this.auth.onAuthStateChanged
  }
  deleteRace(){
    this._raceService.delete(this.race.id);
    this.deleteRelated();
    this._router.navigate([Navigation.Races]);
  }
  addReferee(){
    if(this.newReferee){
      // this._raceService.addReferee(this.race.id, this.newReferee.uid);
      // this.getReferees(this._userUid);
      this.race.referees.push(this.newReferee.uid);
      // make sure distinct
      this.race.referees = Array.from(new Set(this.race.referees));
      // update race
      this._raceService.update(this.race);
      this.getReferees();
      // update related
      this.updateRelated();
      // clean up
      this.newReferee = null;
    }
  }
  removeReferee(referee: User){
    this.race.referees = this.race.referees.filter(uid => uid != referee.uid);
    this._raceService.update(this.race);
    if(!this.race.referees.length){ this.referees=null; }
    this.getReferees();
    this.updateRelated();
    // this._raceService.removeReferee(this.race.id, referee.uid);
    // this.getReferees(this._userUid);
  }
  getReferees(){
    // only authenticated users can view Collection.Users (aka referees)
    if(this.race.owner == this._userUid){
      this.isOwner = true;
      if(this.race.referees?.length){
        this.showPagination = this.race.referees.length > this.refereesPageSize;
        const referees = Object.assign([], this.race.referees);
        const ids = referees.slice((this.refereesPage-1)*this.refereesPageSize, this.refereesPage*this.refereesPageSize);
        if(ids.length){ this.referees = this._userService.getReferees(ids); }
      }
    }
  }
  private getUsers(){
    // only authenticated users can view Collection.Users (aka referees)
    if(this.race.owner == this._userUid){
      this._usersSub = this._userService.list().subscribe(users => {
        this._users = users;
      });
    }
  }
  private updateRelated(){
    const sub = this.events.subscribe(x => {
      // get related event ids
      const eventIds = x.map(y => y.id);
      if(eventIds.length){
        // update events
        this._eventService.updateReferees(this.race.referees, eventIds);
        // update entries
        this._entryService.updateReferees(this.race.referees, eventIds);
        // clean up
        sub.unsubscribe();
      }
    })
  }
  private deleteRelated(){
    const sub = this.events.subscribe(x => {
      // get related event ids
      const eventIds = x.map(y => y.id);
      if(eventIds.length){
        // delete events
        this._eventService.deleteBatch(eventIds);
        // delete entries
        this._entryService.deleteByEvents(eventIds);
        // clean up
        sub.unsubscribe();
      }
    })
  }

  search: OperatorFunction<string, readonly User[]> = (text$: Observable<string>) =>
  text$.pipe(
    debounceTime(300),
    tap(() => this.searching = true),
    map(term => term === '' ? []
      : this._users.filter(v => v.displayName.toLowerCase().indexOf(term.toLowerCase()) > -1).slice(0, 10)),
    tap(() => this.searching = false)
  )
  formatter = (x: User) => x.displayName;

}
